// List + edit user stacks. Stored locally; future Supabase sync will mirror this shape.

import { loadStacks, loadStacksAsync, saveStacks, upsertStack, deleteStack, getActiveStackId, setActiveStackId, newStackId, type UserStack } from '../lib/stack_store';
import { COMPOUND_BY_ID } from '../lib/compounds';
import { whoAmI } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import { scanStack, worstSeverity, SCANNER_DISCLAIMER, type InteractionWarning } from '../lib/interaction_scanner';
import { modalConfirm, modalPrompt } from '../lib/modal';

export async function renderStacks() {
  const app = document.getElementById('app')!;
  // Paint instantly from local cache, then refresh from cloud in the background.
  let stacks = loadStacks();
  const activeId = getActiveStackId();
  paint(app, stacks, activeId);
  const u = await whoAmI();
  if (u) {
    const fresh = await loadStacksAsync();
    if (JSON.stringify(fresh) !== JSON.stringify(stacks)) {
      stacks = fresh;
      paint(app, stacks, getActiveStackId());
    }
  }
}

function paint(app: HTMLElement, stacks: UserStack[], activeId: string | null) {
  app.innerHTML = `
    <div class="detail-header">
      <h1>Stacks</h1>
      <p class="sci">Your saved combinations. Active stack is what \"Add to stack\" lands in by default.</p>
    </div>

    <section class="detail-section">
      <button id="new-stack" class="btn btn-primary">+ New stack</button>
    </section>

    <section class="detail-section">
      ${stacks.length === 0 ? `<div style="padding:20px;background:var(--bg-2);border:1px solid var(--line);border-radius:10px;color:var(--dim);">No stacks yet. Tap "+ New stack" or browse a compound and use \"Add to stack\".</div>` : ''}
      <div class="stack-list" style="display:grid;gap:12px;">
        ${stacks.map((s) => stackCard(s, s.id === activeId)).join('')}
      </div>
    </section>
  `;
  document.getElementById('new-stack')?.addEventListener('click', async () => {
    const name = await modalPrompt({
      title: 'New stack',
      label: 'Name this stack',
      defaultValue: 'My Stack',
      confirmLabel: 'Create',
    });
    if (!name) return;
    const fresh: UserStack = { id: newStackId(), name, goal: null, slot_id: null, items: [] };
    await upsertStack(fresh);
    setActiveStackId(fresh.id);
    renderStacks();
  });
  wireStackCards();
}

function stackCard(s: UserStack, isActive: boolean): string {
  const count = s.items.length;
  const activeCount = s.items.filter((it) => it.active).length;
  const warnings = scanStack(s.items);
  const worst = worstSeverity(warnings);
  const badge = worst
    ? `<button class="warn-badge" data-stack="${esc(s.id)}"
        style="padding:4px 10px;border-radius:14px;font-size:11px;font-weight:700;cursor:pointer;border:1px solid;
               ${worst === 'serious' ? 'background:rgba(226,92,92,0.18);color:#E25C5C;border-color:#E25C5C;'
                 : worst === 'caution' ? 'background:rgba(255,179,71,0.18);color:#FFB347;border-color:#FFB347;'
                 : 'background:rgba(95,196,128,0.18);color:#5FC480;border-color:#5FC480;'}">
        ⚠ ${warnings.length} interaction${warnings.length === 1 ? '' : 's'}
      </button>`
    : '';
  return `
    <div class="stack-card" data-id="${esc(s.id)}"
         style="background:var(--bg-2);border:1px solid ${isActive ? 'var(--acc)' : 'var(--line)'};border-radius:10px;padding:14px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="flex:1;">
          <div style="font-weight:700;font-size:16px;">${esc(s.name)}${isActive ? ' <span style="color:var(--acc);font-size:11px;">· ACTIVE</span>' : ''}</div>
          <div style="color:var(--dim);font-size:12px;">${activeCount} active · ${count} total items${s.goal ? ' · ' + esc(s.goal) : ''}</div>
        </div>
        ${badge}
        <button class="btn make-active" data-id="${esc(s.id)}" ${isActive ? 'disabled' : ''}>${isActive ? 'Active' : 'Make active'}</button>
        <button class="btn delete-stack" data-id="${esc(s.id)}" style="color:#E25C5C;border-color:#E25C5C;">Delete</button>
      </div>
      ${count > 0 ? `<div style="margin-top:10px;display:flex;flex-direction:column;gap:6px;">${s.items.map((it) => itemRow(s.id, it)).join('')}</div>` : ''}
    </div>
  `;
}

function showWarningsModal(stackName: string, warnings: InteractionWarning[]) {
  const back = document.createElement('div');
  back.className = 'ats-back';
  back.innerHTML = `
    <div class="ats-box" style="max-width:600px;max-height:80vh;overflow-y:auto;">
      <h3>${esc(stackName)} — ${warnings.length} interaction${warnings.length === 1 ? '' : 's'} flagged</h3>
      <div style="background:rgba(255,179,71,0.12);border:1px solid #FFB347;border-radius:8px;padding:10px;font-size:12px;color:#FFD08A;line-height:1.5;margin-bottom:14px;">
        ${esc(SCANNER_DISCLAIMER)}
      </div>
      <div style="display:flex;flex-direction:column;gap:10px;">
        ${warnings.map((w) => {
          const color = w.severity === 'serious' ? '#E25C5C' : (w.severity === 'caution' ? '#FFB347' : '#5FC480');
          return `<div style="border:1px solid ${color};border-radius:8px;padding:10px;background:var(--bg-3);">
            <div style="color:${color};font-weight:700;font-size:13px;">${esc(w.shortLabel)} — ${w.severity.toUpperCase()}</div>
            <div style="color:var(--ink);font-size:13px;margin-top:4px;">${esc(w.aName)} + ${esc(w.bName)}</div>
            <div style="color:var(--dim);font-size:12px;margin-top:4px;line-height:1.4;">${esc(w.reason)}</div>
          </div>`;
        }).join('')}
      </div>
      <div class="ats-actions"><button id="warn-close" class="btn">Close</button></div>
    </div>
  `;
  document.body.appendChild(back);
  back.querySelector('#warn-close')?.addEventListener('click', () => back.remove());
  back.addEventListener('click', (e) => { if (e.target === back) back.remove(); });
}

function itemRow(stackId: string, it: UserStack['items'][number]): string {
  const c = COMPOUND_BY_ID[it.compound_id];
  const name = c ? c.display_name : it.compound_id;
  const dose = it.dose_value != null ? `${it.dose_value} ${it.unit}` : (it.dose_mg != null ? `${it.dose_mg} mg` : '');
  return `
    <div style="display:flex;align-items:center;gap:8px;padding:8px;background:var(--bg-3);border-radius:6px;${it.active ? '' : 'opacity:0.5;'}">
      <a href="/c/${esc(it.compound_id)}" style="flex:1;color:var(--ink);text-decoration:none;">
        <span style="font-weight:600;">${esc(name)}</span>
        ${dose ? `<span style="color:var(--dim);font-size:12px;margin-left:8px;">${esc(dose)}</span>` : ''}
        ${it.source ? `<span style="color:var(--dim);font-size:12px;margin-left:8px;">· ${esc(it.source)}</span>` : ''}
        ${it.timing ? `<span style="color:var(--dim);font-size:12px;margin-left:8px;">· ${esc(it.timing)}</span>` : ''}
      </a>
      <button class="btn toggle-active" data-stack="${esc(stackId)}" data-item="${esc(it.id)}" style="padding:4px 10px;font-size:11px;">${it.active ? 'Pause' : 'Resume'}</button>
      <button class="btn remove-item" data-stack="${esc(stackId)}" data-item="${esc(it.id)}" style="padding:4px 10px;font-size:11px;color:#E25C5C;border-color:#E25C5C;">×</button>
    </div>
  `;
}

function wireStackCards() {
  document.querySelectorAll('.warn-badge').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const sId = (b as HTMLElement).dataset.stack;
      if (!sId) return;
      const s = loadStacks().find((x) => x.id === sId);
      if (!s) return;
      const warnings = scanStack(s.items);
      showWarningsModal(s.name, warnings);
    });
  });
  document.querySelectorAll('.make-active').forEach((b) => {
    b.addEventListener('click', () => {
      const id = (b as HTMLElement).dataset.id;
      if (id) { setActiveStackId(id); renderStacks(); }
    });
  });
  document.querySelectorAll('.delete-stack').forEach((b) => {
    b.addEventListener('click', async () => {
      const id = (b as HTMLElement).dataset.id;
      if (!id) return;
      const ok = await modalConfirm({
        title: 'Delete this stack?',
        body: 'All items in it go too. This cannot be undone.',
        confirmLabel: 'Delete',
        danger: true,
      });
      if (!ok) return;
      await deleteStack(id);
      if (getActiveStackId() === id) setActiveStackId(null);
      renderStacks();
    });
  });
  document.querySelectorAll('.toggle-active').forEach((b) => {
    b.addEventListener('click', async () => {
      const sId = (b as HTMLElement).dataset.stack;
      const iId = (b as HTMLElement).dataset.item;
      if (!sId || !iId) return;
      const all = loadStacks();
      const s = all.find((x) => x.id === sId);
      if (!s) return;
      const it = s.items.find((x) => x.id === iId);
      if (!it) return;
      it.active = !it.active;
      await saveStacks(all);
      renderStacks();
    });
  });
  document.querySelectorAll('.remove-item').forEach((b) => {
    b.addEventListener('click', async () => {
      const sId = (b as HTMLElement).dataset.stack;
      const iId = (b as HTMLElement).dataset.item;
      if (!sId || !iId) return;
      const all = loadStacks();
      const s = all.find((x) => x.id === sId);
      if (!s) return;
      const it = s.items.find((x) => x.id === iId);
      if (it) {
        const created = it.created_at ? new Date(it.created_at).getTime() : Date.now();
        const secondsHeld = Math.floor((Date.now() - created) / 1000);
        trackEvent('stack_item_removed', {
          compound_id: it.compound_id,
          source: it.source,
          seconds_held: secondsHeld,
          churn: secondsHeld < 604800,
        });
      }
      s.items = s.items.filter((x) => x.id !== iId);
      await saveStacks(all);
      renderStacks();
    });
  });
}

function esc(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
